import { useCallback, useMemo, useState } from 'react';
import {
  DispatchList,
  DispatchNoteStatus,
  FilterRoute,
  SortDirection,
  dispatch,
  getWorkspaceId,
  removeSearchKey,
  useAppSelector,
  useLazyGetDispatchNoteListQuery,
} from '@zonofi/zono-money-store';
import moment from 'moment';
import {
  useDebounceSearch,
  usePaginatedList,
  useUrlDateRange,
} from '../../hooks';

export const useDispatchNoteList = ({
  isCustomer,
}: {
  isCustomer: boolean;
}) => {
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [fetchDispatchNoteListApi, dispatchNoteListResponse] =
    useLazyGetDispatchNoteListQuery();

  const { appliedDateRange } = useUrlDateRange({
    route: isCustomer ? FilterRoute.CustomerDispatch : FilterRoute.Dispatch,
  });

  const { debouncedSearchValue } = useDebounceSearch();
  const [appliedStatus, setAppliedStatus] = useState<DispatchNoteStatus>(
    DispatchNoteStatus.All
  );

  const memoizedDispatchNoteListData = useMemo(() => {
    return dispatchNoteListResponse?.data?.dispatchList ?? [];
  }, [dispatchNoteListResponse?.data?.dispatchList]);

  const fetchDispatchNoteList = useCallback(
    ({ pageNo, pageSize }: { pageNo: number; pageSize: number }) => {
      fetchDispatchNoteListApi({
        workspaceId: sellerWorkspaceId,
        body: {
          page: pageNo,
          pageSize,
          startDate: `${moment(
            appliedDateRange?.startDate,
            'DD/MM/YYYY'
          ).format('YYYY-MM-DD')} 23:59:59`,
          endDate: `${moment(appliedDateRange?.endDate, 'DD/MM/YYYY').format(
            'YYYY-MM-DD'
          )} 23:59:59`,
          sortBy: 'createdDate',
          ...(appliedStatus &&
          appliedStatus?.length > 0 &&
          appliedStatus !== 'All'
            ? { status: appliedStatus }
            : {}),
          sortDirection: SortDirection.Descending,
          ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
        },
      });
    },
    [appliedDateRange, debouncedSearchValue]
  );

  const {
    data: dispatchNoteListData,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    refresh,
    isEndOfList,
  } = usePaginatedList<DispatchList>({
    fetchDataApiCall: fetchDispatchNoteList,
    apiResponseData: memoizedDispatchNoteListData,
    isLoading: dispatchNoteListResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [
      appliedDateRange?.startDate.toString(),
      appliedDateRange?.endDate.toString(),
      debouncedSearchValue,
      appliedStatus,
    ],
    isError: dispatchNoteListResponse.isError,
    paginationMeta: {
      startRecord: dispatchNoteListResponse?.data?.startRecord || 0,
      endRecord: dispatchNoteListResponse?.data?.endRecord || 0,
      totalCount: dispatchNoteListResponse?.data?.totalRecords || 0,
    },
  });

  const onSearchCancel = () => {
    dispatch(removeSearchKey());
  };

  const statusFilters = useMemo(() => {
    const filters = Object.entries(DispatchNoteStatus).map(([key, val]) => ({
      id: key,
      value: val,
      selected: appliedStatus === val,
    }));

    return filters;
  }, [appliedStatus]);

  return {
    dispatchNoteListData,
    pageNo,
    lastItemRef,
    isPageLoading,
    onScroll,
    isEndOfList,
    isLoading:
      pageNo === 1 &&
      (dispatchNoteListResponse?.isUninitialized ||
        dispatchNoteListResponse?.isFetching),
    onSearchCancel,
    statusFilters,
    setAppliedStatus,
    appliedDateRange,
    refreshDispatchNoteList: refresh,
  };
};
